import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { Document, Page } from "react-pdf";
import { CloseIcon } from "../../../assets/icons";
import {
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";
import {
  fetchTemplateMappings,
  saveTemplateMappings,
  fetchSalesforceFields,
  fetchTransformationChoices,
  fetchPopularFields,
  testMappingPreview,
  searchMatters,
  previewMappingDocument,
  fetchLetterHeadTemplates,
  updateTemplateOutputName,
} from "../../../service/settings";
import PrintPreview from "../../../components/PrintPreview/PrintPreview";

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const SubHeader = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (p.$accent ? "#e0f2fe" : "#f3f4f6")};
  color: ${(p) => (p.$accent ? "#0369a1" : "#374151")};
`;

const ScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -24px;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
`;

const TabPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 12px;
  margin-top: 8px;
`;

const Tab = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: none;
  color: ${(p) => (p.$active ? "#0C53BB" : "#6b7280")};
  border-bottom: 2px solid ${(p) => (p.$active ? "#0C53BB" : "transparent")};
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
  &:hover {
    color: ${(p) => (p.$active ? "#0C53BB" : "#374151")};
  }
`;

const TabCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 9999px;
  background: ${(p) => (p.$active ? "#dbeafe" : "#f3f4f6")};
  color: ${(p) => (p.$active ? "#1d4ed8" : "#6b7280")};
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 1fr 160px;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
`;

const FieldRowHeader = styled(FieldRow)`
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  padding: 10px 0;
`;

const FieldName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
  color: #374151;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;

const SearchableSelectWrapper = styled.div`
  position: relative;
`;

const SearchableInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-top: 2px;
  padding: 0;
  list-style: none;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.li`
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #eff6ff;
  }
`;

const DropdownLabel = styled.span`
  color: #111827;
`;

const DropdownApiName = styled.span`
  color: #9ca3af;
  font-size: 11px;
  margin-left: 6px;
`;

const DropdownDivider = styled.li`
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: default;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  &:first-child {
    border-top: none;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const FooterLeft = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const SaveButton = styled.button`
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  background: #0C53BB;
  color: #fff;
  &:hover {
    background: #1d4ed8;
  }
  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
  color: #374151;
  margin-right: 8px;
  &:hover {
    background: #f9fafb;
  }
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 11px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  &:hover {
    color: #b91c1c;
  }
`;

const Loader = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
  color: #9ca3af;
  font-size: 14px;
`;

// -- Test Preview Panel styles --

const MatterSearchRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const MatterSearchInput = styled.div`
  position: relative;
  flex: 1;
`;

const MatterInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const MatterDropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-top: 2px;
  padding: 0;
  list-style: none;
  z-index: 20;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const MatterItem = styled.li`
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #eff6ff;
  }
`;

const MatterName = styled.span`
  font-weight: 500;
  color: #111827;
`;

const MatterMeta = styled.span`
  color: #9ca3af;
  font-size: 11px;
  margin-left: 8px;
`;

const LetterheadSelect = styled.select`
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  min-width: 160px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const RunButton = styled.button`
  padding: 7px 16px;
  border-radius: 6px;
  border: 1px solid #0C53BB;
  background: #0C53BB;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #1d4ed8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConditionalRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1.2fr auto;
  gap: 8px;
  align-items: start;
  padding: 6px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
`;

const ConditionalRowHeader = styled(ConditionalRow)`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
`;

const ConditionalInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
  &:hover {
    background: #fee2e2;
  }
`;

const AddConditionalButton = styled.button`
  margin-top: 8px;
  padding: 6px 14px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #0C53BB;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
  }
`;

const ConditionalHint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 8px;
  line-height: 1.5;
`;

const RuleCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fff;
`;

const RuleCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const RuleTarget = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const RuleRow = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(170px, 1fr) 130px 130px 140px minmax(170px, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #f3f4f6;
`;

const RuleTargetPicker = styled.div`
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  background: #f0f9ff;
`;

const RuleTargetPickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #075985;
`;

const RuleTargetSearch = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #7dd3fc;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  background: #fff;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const RuleTargetList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 6px 10px;
  max-height: 150px;
  overflow-y: auto;
  margin-top: 8px;
  padding-right: 4px;
`;

const RuleTargetOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #0f172a;
  cursor: pointer;
  background: ${(p) => (p.$selected ? "#dbeafe" : "#fff")};
  border: 1px solid ${(p) => (p.$selected ? "#93c5fd" : "#e0f2fe")};
  &:hover {
    border-color: #93c5fd;
  }
`;

const RuleTargetName = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RuleTargetApiName = styled.span`
  color: #64748b;
  font-size: 10px;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RuleNote = styled.div`
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 12px;
`;

const DocumentPreviewWrapper = styled.div`
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
`;

const DocumentPreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const ViewFullButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #0C53BB;
  background: #eff6ff;
  color: #0C53BB;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #dbeafe;
  }
`;

const OutputNameSection = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const OutputNameLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const OutputNameInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const OutputNameHint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

const OutputNameSaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const SmallButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$primary ? "#0C53BB" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#374151")};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SavedIndicator = styled.span`
  font-size: 11px;
  color: #16a34a;
  font-weight: 500;
`;

// ---------------------------------------------------------------------------
// Searchable SF Field Select
// ---------------------------------------------------------------------------

export function SalesforceFieldSelect({ value, onChange, fields, disabled, popularNames }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedField = useMemo(
    () => fields.find((f) => f.name === value),
    [fields, value]
  );

  const popularSet = useMemo(
    () => new Set(popularNames || []),
    [popularNames]
  );

  const { popular, filtered } = useMemo(() => {
    const q = search.toLowerCase();
    const matchFn = (f) =>
      !q || f.label.toLowerCase().includes(q) || f.name.toLowerCase().includes(q);

    const pop = [];
    const rest = [];
    for (const f of fields) {
      if (!matchFn(f)) continue;
      if (popularSet.has(f.name)) pop.push(f);
      else rest.push(f);
    }
    return {
      popular: pop.slice(0, 10),
      filtered: rest,
    };
  }, [fields, search, popularSet]);

  const displayValue = selectedField ? `${selectedField.label}` : "";
  const hasPopular = popular.length > 0;
  const noResults = popular.length === 0 && filtered.length === 0;

  return (
    <SearchableSelectWrapper>
      <Tooltip title={value ? `API: ${value}` : "Select a Salesforce field"} arrow>
        <SearchableInput
          value={open ? search : displayValue}
          placeholder={disabled ? "" : "Search fields..."}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            setSearch("");
          }}
          onChange={(e) => setSearch(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
      </Tooltip>
      {open && !disabled && (
        <DropdownList>
          {noResults && search && search.includes("__") && (
            <DropdownItem
              onMouseDown={() => {
                onChange(search.trim());
                setOpen(false);
              }}
              style={{ background: "#f0f9ff" }}
            >
              <DropdownLabel>Use &quot;{search.trim()}&quot;</DropdownLabel>
              <DropdownApiName>custom field name</DropdownApiName>
            </DropdownItem>
          )}
          {noResults && !(search && search.includes("__")) && (
            <DropdownItem style={{ color: "#9ca3af", cursor: "default" }}>
              No fields match
            </DropdownItem>
          )}
          {hasPopular && (
            <>
              <DropdownDivider>Commonly Used</DropdownDivider>
              {popular.map((f) => (
                <DropdownItem
                  key={`pop-${f.name}`}
                  onMouseDown={() => {
                    onChange(f.name);
                    setOpen(false);
                  }}
                >
                  <DropdownLabel>{f.label}</DropdownLabel>
                  <DropdownApiName>{f.name}</DropdownApiName>
                </DropdownItem>
              ))}
            </>
          )}
          {filtered.length > 0 && (
            <>
              {hasPopular && <DropdownDivider>All Fields</DropdownDivider>}
              {filtered.map((f) => (
                <DropdownItem
                  key={f.name}
                  onMouseDown={() => {
                    onChange(f.name);
                    setOpen(false);
                  }}
                >
                  <DropdownLabel>{f.label}</DropdownLabel>
                  <DropdownApiName>{f.name}</DropdownApiName>
                </DropdownItem>
              ))}
            </>
          )}
        </DropdownList>
      )}
    </SearchableSelectWrapper>
  );
}

SalesforceFieldSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fields: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  popularNames: PropTypes.arrayOf(PropTypes.string),
};

function TemplateFieldMultiSelect({
  fields,
  selectedFieldNames,
  searchValue,
  onSearchChange,
  onToggleField,
}) {
  const selectedSet = useMemo(
    () => new Set(selectedFieldNames || []),
    [selectedFieldNames]
  );
  const filteredFields = useMemo(() => {
    const q = (searchValue || "").toLowerCase().trim();
    if (!q) return fields;
    return fields.filter((field) => {
      const label = field.field_label || field.field_name || "";
      const name = field.field_name || "";
      return label.toLowerCase().includes(q) || name.toLowerCase().includes(q);
    });
  }, [fields, searchValue]);

  return (
    <RuleTargetPicker>
      <RuleTargetPickerHeader>
        <span>Apply this rule to template fields</span>
        <Badge $accent>{selectedSet.size} selected</Badge>
      </RuleTargetPickerHeader>
      <RuleTargetSearch
        value={searchValue || ""}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search template fields..."
      />
      <RuleTargetList>
        {filteredFields.length === 0 ? (
          <ConditionalHint style={{ gridColumn: "1 / -1", marginTop: 0 }}>
            No template fields match this search.
          </ConditionalHint>
        ) : (
          filteredFields.map((field) => {
            const selected = selectedSet.has(field.field_name);
            return (
              <RuleTargetOption key={field.field_name} $selected={selected}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => onToggleField(field.field_name, e.target.checked)}
                />
                <RuleTargetName>
                  <span>{humanize(field.field_label || field.field_name)}</span>
                  <RuleTargetApiName>{field.field_name}</RuleTargetApiName>
                </RuleTargetName>
              </RuleTargetOption>
            );
          })
        )}
      </RuleTargetList>
    </RuleTargetPicker>
  );
}

TemplateFieldMultiSelect.propTypes = {
  fields: PropTypes.array.isRequired,
  selectedFieldNames: PropTypes.array.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onToggleField: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Mapping helpers – which SF objects to query for each data source
// ---------------------------------------------------------------------------

export const SOURCE_TO_OBJECT = {
  ACCOUNT_VETERAN: "account",
  ACCOUNT_CLAIMANT: "account",
  ACCOUNT_CLIENT: "account",
  USER_ATTORNEY: "user",
  MATTER: "matter",
  INTAKE: "intake",
};

// ---------------------------------------------------------------------------
// Auto-suggest engine
// ---------------------------------------------------------------------------

const KEYWORD_TO_SOURCE = [
  { keywords: ["veteran", "vet_"], source: "ACCOUNT_VETERAN" },
  { keywords: ["claimant", "clmt"], source: "ACCOUNT_CLAIMANT" },
  { keywords: ["client"], source: "ACCOUNT_CLIENT" },
  { keywords: ["attorney", "atty", "lawyer", "counsel"], source: "USER_ATTORNEY" },
  { keywords: ["matter", "case", "docket"], source: "MATTER" },
];

const KEYWORD_TO_TRANSFORM = [
  { keywords: ["first name", "firstname", "first_name"], transform: "split_name_first" },
  { keywords: ["last name", "lastname", "last_name"], transform: "split_name_last" },
  { keywords: ["middle name", "middlename", "middle_name", "middle initial", "middleinitial"], transform: "split_name_middle" },
  { keywords: ["area code", "areacode"], transform: "split_phone_first" },
  { keywords: ["exchange"], transform: "split_phone_middle" },
];

export function humanize(str) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalize(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseUnmappedHeaderNames(rawHeaderValue) {
  if (!rawHeaderValue || typeof rawHeaderValue !== "string") return [];
  return rawHeaderValue
    .split(",")
    .map((rawName) => {
      try {
        return decodeURIComponent(rawName);
      } catch (_decodeErr) {
        return rawName;
      }
    })
    .map((name) => name.trim())
    .filter(Boolean);
}

function parseUnmappedHeaderCount(rawHeaderValue) {
  const parsed = Number(rawHeaderValue);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed);
}

export function createDefaultLogicRuleCondition() {
  return {
    _key: `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    left: { object: "MATTER", field: "" },
    operator: "equals",
    right: { type: "literal", value: false },
  };
}

export function createDefaultLogicRules() {
  return {
    mode: "all",
    conditions: [createDefaultLogicRuleCondition()],
  };
}

export function createDefaultLogicRuleGroup() {
  return {
    _key: `rule-group-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    ...createDefaultLogicRules(),
    targetFieldNames: [],
  };
}

function isCompleteLogicCondition(condition) {
  if (!condition?.left?.object || !condition?.left?.field) return false;
  if (!["equals", "does_not_equal"].includes(condition.operator)) return false;
  if (condition.right?.type === "literal") {
    return condition.right.value === true || condition.right.value === false;
  }
  if (condition.right?.type === "field") {
    return Boolean(condition.right.object && condition.right.field);
  }
  return false;
}

export function sanitizeLogicRulesForSave(rules) {
  if (!rules || !Array.isArray(rules.conditions)) return {};
  const conditions = rules.conditions
    .filter(isCompleteLogicCondition)
    .map((condition) => ({
      left: {
        object: condition.left.object,
        field: condition.left.field,
      },
      operator: condition.operator,
      right:
        condition.right.type === "field"
          ? {
              type: "field",
              object: condition.right.object,
              field: condition.right.field,
            }
          : {
              type: "literal",
              value: condition.right.value === true,
            },
    }));

  if (conditions.length === 0) return {};
  return {
    mode: rules.mode === "any" ? "any" : "all",
    conditions,
  };
}

export function countMappingsWithLogicRules(mappings) {
  return Object.values(mappings || {}).filter(
    (mapping) => sanitizeLogicRulesForSave(mapping.logic_rules).conditions?.length
  ).length;
}

function cloneLogicRulesForUi(rules) {
  const cleanRules = sanitizeLogicRulesForSave(rules);
  if (!cleanRules.conditions?.length) return createDefaultLogicRules();
  return {
    mode: cleanRules.mode,
    conditions: cleanRules.conditions.map((condition) => ({
      _key: `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      left: { ...condition.left },
      operator: condition.operator,
      right: { ...condition.right },
    })),
  };
}

export function buildLogicRuleGroupsFromMappings(mappings) {
  const grouped = new Map();
  Object.entries(mappings || {}).forEach(([fieldName, mapping]) => {
    const cleanRules = sanitizeLogicRulesForSave(mapping.logic_rules);
    if (!cleanRules.conditions?.length) return;
    const key = JSON.stringify(cleanRules);
    if (!grouped.has(key)) {
      grouped.set(key, {
        _key: `rule-group-${grouped.size + 1}`,
        ...cloneLogicRulesForUi(cleanRules),
        targetFieldNames: [],
      });
    }
    grouped.get(key).targetFieldNames.push(fieldName);
  });
  return Array.from(grouped.values());
}

export function buildLogicRulesByField(groups, validFieldNames) {
  const validNames = validFieldNames ? new Set(validFieldNames) : null;
  const rulesByField = {};
  (groups || []).forEach((group) => {
    const cleanRules = sanitizeLogicRulesForSave(group);
    if (!cleanRules.conditions?.length) return;
    (group.targetFieldNames || []).forEach((fieldName) => {
      if (validNames && !validNames.has(fieldName)) return;
      rulesByField[fieldName] = cleanRules;
    });
  });
  return rulesByField;
}

export function countFieldsWithLogicRuleGroups(groups, validFieldNames) {
  return Object.keys(buildLogicRulesByField(groups, validFieldNames)).length;
}

function collectRuleObjectTypes(rules) {
  const objectTypes = new Set();
  (rules?.conditions || []).forEach((condition) => {
    const leftType = SOURCE_TO_OBJECT[condition?.left?.object];
    if (leftType) objectTypes.add(leftType);
    const rightType = SOURCE_TO_OBJECT[condition?.right?.object];
    if (rightType) objectTypes.add(rightType);
  });
  return objectTypes;
}

export function suggestMapping(fieldName, sfFieldsByObject) {
  const lower = fieldName.toLowerCase();
  const human = humanize(fieldName).toLowerCase();

  let bestSource = "";
  for (const rule of KEYWORD_TO_SOURCE) {
    if (rule.keywords.some((k) => lower.includes(k) || human.includes(k))) {
      bestSource = rule.source;
      break;
    }
  }

  if (!bestSource) return null;

  const objType = SOURCE_TO_OBJECT[bestSource];
  const fields = sfFieldsByObject[objType] || [];
  if (fields.length === 0) return null;

  let bestTransform = "";
  for (const rule of KEYWORD_TO_TRANSFORM) {
    if (rule.keywords.some((k) => lower.includes(normalize(k)) || human.includes(k))) {
      bestTransform = rule.transform;
      break;
    }
  }

  const norm = normalize(human);
  const nameFragments = human.split(/\s+/).filter((w) => w.length > 2);

  let bestField = null;
  let bestScore = 0;
  for (const sf of fields) {
    const sfNorm = normalize(sf.label);
    const sfNameNorm = normalize(sf.name);
    let score = 0;

    if (sfNorm === norm || sfNameNorm === norm) {
      score = 100;
    } else {
      for (const frag of nameFragments) {
        if (sfNorm.includes(frag) || sfNameNorm.includes(frag)) score += 20;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestField = sf;
    }
  }

  if (!bestField || bestScore < 20) return null;

  return {
    sf_object: bestSource,
    sf_field_name: bestField.name,
    transformation: bestTransform,
  };
}

// ---------------------------------------------------------------------------
// Styled toolbar
// ---------------------------------------------------------------------------

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: ${(p) => (p.$primary ? "#eff6ff" : "#fff")};
  color: ${(p) => (p.$primary ? "#0C53BB" : p.$danger ? "#dc2626" : "#374151")};
  border-color: ${(p) => (p.$primary ? "#bfdbfe" : p.$danger ? "#fca5a5" : "#d1d5db")};
  &:hover {
    background: ${(p) => (p.$primary ? "#dbeafe" : p.$danger ? "#fef2f2" : "#f9fafb")};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// FieldMappingModal
// ---------------------------------------------------------------------------

export default function FieldMappingModal({ open, template, onClose, onSaved, initialTab = "fields" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const [mappings, setMappings] = useState({});
  const [dataSources, setDataSources] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [sfFieldsCache, setSfFieldsCache] = useState({});
  const [popularFieldsByObject, setPopularFieldsByObject] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("all");

  // -- Test preview state --
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [matterDropdownOpen, setMatterDropdownOpen] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [matterSearching, setMatterSearching] = useState(false);

  // -- Document preview state --
  const [documentPreview, setDocumentPreview] = useState(null);
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);
  const [docPreviewError, setDocPreviewError] = useState(null);
  const [, setDocUnmappedFieldNames] = useState([]);
  const [, setDocUnmappedFieldCount] = useState(0);
  const [inlinePreviewPageCount, setInlinePreviewPageCount] = useState(0);
  const [inlinePreviewRenderError, setInlinePreviewRenderError] = useState(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [letterheads, setLetterheads] = useState([]);
  const [selectedLetterhead, setSelectedLetterhead] = useState("");

  // -- Conditional sections state --
  const [conditionalEntries, setConditionalEntries] = useState([]);
  const [mergeFieldEntries, setMergeFieldEntries] = useState([]);
  const [logicRuleGroups, setLogicRuleGroups] = useState([]);
  const [ruleTargetSearchByGroup, setRuleTargetSearchByGroup] = useState({});

  const [saveError, setSaveError] = useState(null);

  // -- Tab state --
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  // -- Output name state --
  const [outputName, setOutputName] = useState("");
  const [outputNameDirty, setOutputNameDirty] = useState(false);
  const [outputNameSaving, setOutputNameSaving] = useState(false);
  const [outputNameSaved, setOutputNameSaved] = useState(false);
  const previewRunIdRef = useRef(0);
  const isMountedRef = useRef(true);

  const formstackId = template?.formstack_id;
  const inlinePreviewFile = useMemo(() => {
    const base64Content = documentPreview?.file_content;
    if (!base64Content) return null;
    try {
      const binary = atob(base64Content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return { data: bytes };
    } catch (error) {
      console.error("Failed to decode inline PDF preview", error);
      return null;
    }
  }, [documentPreview?.file_content]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ---- Initial data load ----
  useEffect(() => {
    if (!open || !formstackId) return;
    let cancelled = false;

    setOutputName(template?.output_name || "");
    setOutputNameDirty(false);
    setOutputNameSaved(false);

    async function load() {
      setLoading(true);
      try {
        const [mappingRes, choicesRes, popularRes] = await Promise.all([
          fetchTemplateMappings(formstackId),
          fetchTransformationChoices(),
          fetchPopularFields().catch(() => null),
        ]);

        if (cancelled) return;

        const mappingData = mappingRes?.data || mappingRes;
        const choicesData = choicesRes?.data || choicesRes;

        setFields(mappingData.fields || []);
        setDataSources(choicesData.data_sources || []);
        setTransformations(choicesData.transformations || []);

        if (popularRes) {
          const popData = popularRes?.data || popularRes;
          const byObj = popData.popular_fields || {};
          const namesByObj = {};
          for (const [obj, items] of Object.entries(byObj)) {
            namesByObj[obj] = items.map((i) => i.name);
          }
          setPopularFieldsByObject(namesByObj);
        }

        const initial = {};
        const neededObjectTypes = new Set();
        (mappingData.fields || []).forEach((f) => {
          if (f.mapping) {
            const savedLogicRules = sanitizeLogicRulesForSave(f.mapping.logic_rules);
            initial[f.field_name] = {
              sf_object: f.mapping.sf_object,
              sf_field_name: f.mapping.sf_field_name,
              transformation: f.mapping.transformation || "",
              logic_rules: savedLogicRules.conditions?.length ? savedLogicRules : {},
            };
            const objType = SOURCE_TO_OBJECT[f.mapping.sf_object];
            if (objType) neededObjectTypes.add(objType);
            collectRuleObjectTypes(savedLogicRules).forEach((type) => neededObjectTypes.add(type));
          }
        });
        setMappings(initial);
        setLogicRuleGroups(buildLogicRuleGroupsFromMappings(initial));
        setRuleTargetSearchByGroup({});

        const savedConditionals = (mappingData.conditional_mappings || []).map((c, idx) => ({
          _key: `cond-${Date.now()}-${idx}`,
          field_name: c.template_field_name || "",
          sf_object: c.sf_object || "",
          sf_field_name: c.sf_field_name || "",
          conditional_notes: c.conditional_notes || "",
        }));
        setConditionalEntries(savedConditionals);

        const savedMergeFields = (mappingData.merge_field_mappings || []).map((m, idx) => ({
          _key: `merge-${Date.now()}-${idx}`,
          field_name: m.template_field_name || "",
          sf_object: m.sf_object || "",
          sf_field_name: m.sf_field_name || "",
          transformation: m.transformation || "",
        }));
        setMergeFieldEntries(savedMergeFields);

        savedConditionals.forEach((c) => {
          const objType = SOURCE_TO_OBJECT[c.sf_object];
          if (objType) neededObjectTypes.add(objType);
        });
        savedMergeFields.forEach((m) => {
          const objType = SOURCE_TO_OBJECT[m.sf_object];
          if (objType) neededObjectTypes.add(objType);
        });

        neededObjectTypes.forEach((objType) => {
          fetchSalesforceFields(objType)
            .then((res) => {
              const data = res?.data || res;
              setSfFieldsCache((prev) => ({ ...prev, [objType]: data.fields || [] }));
            })
            .catch((err) => console.error(`Failed to preload SF fields for ${objType}`, err));
        });
      } catch (err) {
        console.error("Failed to load mapping data", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, formstackId]);

  // ---- Lazy-load SF fields per object type ----
  const loadSfFields = useCallback(
    async (objectType, { refresh } = {}) => {
      if (!refresh && sfFieldsCache[objectType]) return;
      try {
        const res = await fetchSalesforceFields(objectType, { refresh });
        const data = res?.data || res;
        setSfFieldsCache((prev) => ({ ...prev, [objectType]: data.fields || [] }));
      } catch (err) {
        console.error(`Failed to load SF fields for ${objectType}`, err);
      }
    },
    [sfFieldsCache]
  );

  const refreshAllSfFields = useCallback(async () => {
    const types = [...new Set(Object.values(SOURCE_TO_OBJECT))];
    await Promise.all(types.map((t) => loadSfFields(t, { refresh: true })));
  }, [loadSfFields]);

  const groupedDataSources = useMemo(() => {
    const groups = [];
    const seen = new Set();
    dataSources.forEach((ds) => {
      const g = ds.group || "";
      if (!seen.has(g)) {
        seen.add(g);
        groups.push({ group: g, items: [] });
      }
      groups.find((gr) => gr.group === g).items.push(ds);
    });
    return groups;
  }, [dataSources]);

  // ---- Field update helpers ----
  const updateMapping = useCallback((fieldName, key, value) => {
    setMappings((prev) => {
      const existing = prev[fieldName] || {
        sf_object: "",
        sf_field_name: "",
        transformation: "",
      };
      const updated = { ...existing, [key]: value };

      if (key === "sf_object") {
        updated.sf_field_name = "";
        if (value) {
          const objType = SOURCE_TO_OBJECT[value];
          if (objType) loadSfFields(objType);
        }
      }

      if (!updated.sf_object && !updated.sf_field_name && !updated.transformation) {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      }
      return { ...prev, [fieldName]: updated };
    });
  }, [loadSfFields]);

  const clearMapping = useCallback((fieldName) => {
    setMappings((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const clearAllMappings = useCallback(() => {
    setMappings({});
  }, []);

  const updateLogicRuleGroup = useCallback((groupKey, updater) => {
    setLogicRuleGroups((prev) =>
      prev.map((group) => {
        if (group._key !== groupKey) return group;
        return typeof updater === "function" ? updater(group) : { ...group, ...updater };
      })
    );
  }, []);

  const addLogicRuleGroup = useCallback(() => {
    const nextGroup = createDefaultLogicRuleGroup();
    setLogicRuleGroups((prev) => [...prev, nextGroup]);
    setRuleTargetSearchByGroup((prev) => ({ ...prev, [nextGroup._key]: "" }));
  }, []);

  const removeLogicRuleGroup = useCallback((groupKey) => {
    setLogicRuleGroups((prev) => {
      const remaining = prev.filter((group) => group._key !== groupKey);
      return remaining.length > 0 ? remaining : [createDefaultLogicRuleGroup()];
    });
    setRuleTargetSearchByGroup((prev) => {
      const next = { ...prev };
      delete next[groupKey];
      return next;
    });
  }, []);

  const addLogicRuleCondition = useCallback((groupKey) => {
    updateLogicRuleGroup(groupKey, (group) => ({
      ...group,
      conditions: [...(group.conditions || []), createDefaultLogicRuleCondition()],
    }));
  }, [updateLogicRuleGroup]);

  const removeLogicRuleCondition = useCallback((groupKey, conditionIndex) => {
    updateLogicRuleGroup(groupKey, (group) => {
      const remaining = (group.conditions || []).filter((_, idx) => idx !== conditionIndex);
      return {
        ...group,
        conditions: remaining.length > 0 ? remaining : [createDefaultLogicRuleCondition()],
      };
    });
  }, [updateLogicRuleGroup]);

  const updateLogicRuleCondition = useCallback((groupKey, conditionIndex, updater) => {
    updateLogicRuleGroup(groupKey, (group) => ({
      ...group,
      conditions: (group.conditions || []).map((condition, idx) =>
        idx === conditionIndex ? updater(condition) : condition
      ),
    }));
  }, [updateLogicRuleGroup]);

  const toggleLogicRuleTarget = useCallback((groupKey, fieldName, selected) => {
    setLogicRuleGroups((prev) =>
      prev.map((group) => {
        const currentTargets = group.targetFieldNames || [];
        if (group._key === groupKey) {
          const nextTargets = selected
            ? [...new Set([...currentTargets, fieldName])]
            : currentTargets.filter((name) => name !== fieldName);
          return { ...group, targetFieldNames: nextTargets };
        }
        if (!selected) return group;
        return {
          ...group,
          targetFieldNames: currentTargets.filter((name) => name !== fieldName),
        };
      })
    );
  }, []);

  // Pre-fetch all SF field sets so auto-suggest has data to work with
  const preloadAllSfFields = useCallback(async () => {
    const needed = ["account", "user", "matter", "intake"];
    const promises = needed
      .filter((t) => !sfFieldsCache[t])
      .map((t) => fetchSalesforceFields(t).then((res) => {
        const data = res?.data || res;
        return { type: t, fields: data.fields || [] };
      }));
    if (promises.length === 0) return sfFieldsCache;

    const results = await Promise.all(promises);
    const merged = { ...sfFieldsCache };
    results.forEach((r) => { merged[r.type] = r.fields; });
    setSfFieldsCache(merged);
    return merged;
  }, [sfFieldsCache]);

  // ---- Load letterheads on open ----
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function loadLetterheads() {
      try {
        const res = await fetchLetterHeadTemplates();
        if (cancelled) return;
        const data = res?.data?.data || res?.data || [];
        const active = Array.isArray(data) ? data.filter((lh) => lh.is_active) : [];
        setLetterheads(active);
      } catch (err) {
        console.error("Failed to load letterheads", err);
      }
    }
    loadLetterheads();
    return () => { cancelled = true; };
  }, [open]);

  // -- Matter search with debounce --
  useEffect(() => {
    if (!matterQuery || matterQuery.length < 2) {
      setMatterResults([]);
      return;
    }
    setMatterSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchMatters(matterQuery);
        const data = res?.data || res;
        setMatterResults(data?.results || data || []);
        setMatterDropdownOpen(true);
      } catch (err) {
        console.error("Matter search failed", err);
      } finally {
        setMatterSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [matterQuery]);

  const runPreview = useCallback(async () => {
    if (!selectedMatter || !formstackId) return;
    previewRunIdRef.current += 1;
    const runId = previewRunIdRef.current;
    const isStaleRun = () =>
      !isMountedRef.current || previewRunIdRef.current !== runId;

    const toDisplayString = (value) => {
      if (value == null) return null;
      if (typeof value === "string") return value.trim() || null;
      try {
        return JSON.stringify(value).slice(0, 300);
      } catch (_stringifyErr) {
        return String(value).slice(0, 300);
      }
    };

    const addStatusPrefix = (message, err) => {
      const statusCode = err?.response?.status;
      if (!statusCode) return message;
      return `HTTP ${statusCode}: ${message}`;
    };

    const parseTextError = (text) => {
      if (!text || typeof text !== "string") return null;
      const trimmed = text.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          return (
            toDisplayString(parsed.error || parsed.detail || parsed.message) ||
            trimmed.slice(0, 300)
          );
        } catch (_parseErr) {
          return trimmed.slice(0, 300);
        }
      }
      return trimmed.slice(0, 300);
    };

    const parseErrorBody = async (err) => {
      let serverMessage = null;
      const responseData = err?.response?.data;
      if (!responseData) return null;

      try {
        if (responseData instanceof Blob) {
          const text = await responseData.text();
          serverMessage = parseTextError(text);
        } else if (typeof responseData === "object") {
          serverMessage = toDisplayString(
            responseData.error || responseData.detail || responseData.message
          );
        } else if (typeof responseData === "string" && responseData.trim()) {
          serverMessage = responseData.slice(0, 300);
        }
      } catch (parseErr) {
        console.warn("Failed to parse preview error body", parseErr);
      }

      return serverMessage;
    };

    const shouldRetryDocumentPreview = (err, attempt) => {
      if (attempt >= 2) return false;
      if (err?.response?.status) return false;
      const message = String(err?.message || "").toLowerCase();
      return (
        message.includes("network error") ||
        message.includes("failed to fetch") ||
        message.includes("timeout")
      );
    };

    setPreviewLoading(true);
    setDocPreviewLoading(true);
    setPreviewData(null);
    setPreviewError(null);
    setDocumentPreview(null);
    setDocPreviewError(null);
    setDocUnmappedFieldNames([]);
    setDocUnmappedFieldCount(0);
    setInlinePreviewPageCount(0);
    setInlinePreviewRenderError(null);

    const tablePromise = testMappingPreview(formstackId, selectedMatter.id)
      .then((res) => {
        if (isStaleRun()) return;
        setPreviewData(res?.data || res);
      })
      .catch((err) => {
        console.error("Table preview failed", err);
        if (isStaleRun()) return;
        const parsed = toDisplayString(
          err?.response?.data?.error ||
            err?.response?.data?.detail ||
            err?.response?.data?.message
        );
        setPreviewError(
          addStatusPrefix(
            parsed || err?.message || "Failed to fetch field-value preview.",
            err
          )
        );
      })
      .finally(() => {
        if (isStaleRun()) return;
        setPreviewLoading(false);
      });

    const fetchDocPreview = async (attempt = 1) => {
      try {
        const res = await previewMappingDocument(
          formstackId,
          selectedMatter.id,
          selectedLetterhead || undefined
        );
        const parsedHeaderNames = parseUnmappedHeaderNames(
          res?.headers?.["x-unmapped-field-names"]
        );
        const parsedHeaderCount = parseUnmappedHeaderCount(
          res?.headers?.["x-unmapped-field-count"]
        );
        const blob = res?.data || res;
        if (!blob || !(blob instanceof Blob) || blob.size === 0) {
          throw new Error("Empty or invalid response");
        }
        if (blob.type && blob.type.includes("json")) {
          const text = await blob.text();
          throw new Error(parseTextError(text) || "Server error");
        }
        const signature = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
        const isPdf =
          signature.length === 5 &&
          signature[0] === 0x25 && // %
          signature[1] === 0x50 && // P
          signature[2] === 0x44 && // D
          signature[3] === 0x46 && // F
          signature[4] === 0x2d; // -
        if (!isPdf) {
          const text = await blob.text();
          throw new Error(
            parseTextError(text) || "Server returned non-PDF preview content"
          );
        }
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result?.split(",")[1]);
          reader.onerror = () => reject(new Error("FileReader failed"));
          reader.readAsDataURL(blob);
        });
        if (!base64) throw new Error("Base64 conversion produced empty result");
        if (isStaleRun()) return;
        setDocUnmappedFieldNames(parsedHeaderNames);
        setDocUnmappedFieldCount(
          Math.max(parsedHeaderCount, parsedHeaderNames.length)
        );
        setDocumentPreview({
          file_content: base64,
          file_type: "pdf",
          content_type: "application/pdf",
          filename: `preview_${formstackId}.pdf`,
        });
      } catch (err) {
        if (isStaleRun()) return;
        const serverMessage = await parseErrorBody(err);
        if (isStaleRun()) return;

        if (shouldRetryDocumentPreview(err, attempt)) {
          console.warn(
            `Document preview attempt ${attempt} failed, retrying...`,
            serverMessage || err.message
          );
          await new Promise((r) => setTimeout(r, 2000));
          if (isStaleRun()) return;
          return fetchDocPreview(attempt + 1);
        }

        console.error("Document preview failed", err);
        setDocPreviewError(
          addStatusPrefix(
            serverMessage ||
              err.message ||
              "Document preview generation failed. Try again.",
            err
          )
        );
      }
    };

    const docPromise = fetchDocPreview().finally(() => {
      if (isStaleRun()) return;
      setDocPreviewLoading(false);
    });

    await Promise.allSettled([tablePromise, docPromise]);
  }, [selectedMatter, formstackId, selectedLetterhead]);

  const autoSuggestAll = useCallback(async () => {
    setLoading(true);
    try {
      const allFields = await preloadAllSfFields();
      const suggested = {};
      fields.forEach((f) => {
        if (mappings[f.field_name]) return; // don't overwrite existing
        const result = suggestMapping(f.field_name, allFields);
        if (result) suggested[f.field_name] = result;
      });
      setMappings((prev) => ({ ...prev, ...suggested }));
    } catch (err) {
      console.error("Auto-suggest failed", err);
    } finally {
      setLoading(false);
    }
  }, [fields, mappings, preloadAllSfFields]);

  // ---- Save ----
  const handleSaveOutputName = async () => {
    setOutputNameSaving(true);
    try {
      await updateTemplateOutputName(formstackId, outputName);
      setOutputNameDirty(false);
      setOutputNameSaved(true);
      setTimeout(() => setOutputNameSaved(false), 2500);
    } catch (err) {
      console.error("Failed to save output name", err);
    } finally {
      setOutputNameSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const mappedFieldNames = Object.entries(mappings)
        .filter(([, m]) => m.sf_object && m.sf_field_name)
        .map(([fieldName]) => fieldName);
      const logicRulesByField = buildLogicRulesByField(logicRuleGroups, mappedFieldNames);
      const payload = Object.entries(mappings).map(([fieldName, m]) => ({
        template_field_name: fieldName,
        sf_object: m.sf_object,
        sf_field_name: m.sf_field_name,
        transformation: m.transformation || "",
        logic_rules: logicRulesByField[fieldName] || {},
      }));

      const conditionalPayload = conditionalEntries
        .filter((c) => c.field_name && c.sf_object && c.sf_field_name)
        .map((c) => ({
          template_field_name: c.field_name,
          sf_object: c.sf_object,
          sf_field_name: c.sf_field_name,
          transformation: "",
          is_conditional: true,
          conditional_notes: c.conditional_notes || "",
        }));

      const mergeFieldPayload = mergeFieldEntries
        .filter((m) => m.field_name && m.sf_object && m.sf_field_name)
        .map((m) => ({
          template_field_name: m.field_name,
          sf_object: m.sf_object,
          sf_field_name: m.sf_field_name,
          transformation: m.transformation || "",
        }));

      if (outputNameDirty) {
        await updateTemplateOutputName(formstackId, outputName);
      }

      await saveTemplateMappings(formstackId, [...payload, ...conditionalPayload, ...mergeFieldPayload]);

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Failed to save mappings", err);
      setSaveError("Failed to save template configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- Filtering / searching ----
  const visibleFields = useMemo(() => {
    let result = fields;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.field_name?.toLowerCase().includes(q) ||
          f.field_label?.toLowerCase().includes(q)
      );
    }
    if (filterMode === "mapped") {
      result = result.filter((f) => mappings[f.field_name]);
    } else if (filterMode === "unmapped") {
      result = result.filter((f) => !mappings[f.field_name]);
    }
    return result;
  }, [fields, searchQuery, filterMode, mappings]);

  const mappedFieldsForRules = useMemo(
    () =>
      fields.filter((field) => {
        const mapping = mappings[field.field_name];
        return Boolean(mapping?.sf_object && mapping?.sf_field_name);
      }),
    [fields, mappings]
  );
  const mappedFieldNamesForRules = useMemo(
    () => mappedFieldsForRules.map((field) => field.field_name),
    [mappedFieldsForRules]
  );
  const visibleLogicRuleGroups = useMemo(() => {
    if (logicRuleGroups.length > 0) return logicRuleGroups;
    return mappedFieldsForRules.length > 0 ? [createDefaultLogicRuleGroup()] : [];
  }, [logicRuleGroups, mappedFieldsForRules.length]);

  useEffect(() => {
    if (mappedFieldsForRules.length === 0 || logicRuleGroups.length > 0) return;
    setLogicRuleGroups([createDefaultLogicRuleGroup()]);
  }, [mappedFieldsForRules.length, logicRuleGroups.length]);

  const ruleObjectTypesToPreload = useMemo(() => {
    if (activeTab !== "rules" || mappedFieldsForRules.length === 0) return [];
    const objectTypes = new Set();
    const groups = logicRuleGroups.length > 0 ? logicRuleGroups : [createDefaultLogicRules()];
    groups.forEach((group) => {
      collectRuleObjectTypes(group).forEach((objectType) => objectTypes.add(objectType));
    });
    return Array.from(objectTypes);
  }, [activeTab, logicRuleGroups, mappedFieldsForRules.length]);

  useEffect(() => {
    ruleObjectTypesToPreload.forEach((objectType) => {
      loadSfFields(objectType);
    });
  }, [ruleObjectTypesToPreload, loadSfFields]);

  const mappedCount = Object.keys(mappings).length;
  const logicRulesCount = countFieldsWithLogicRuleGroups(
    logicRuleGroups,
    mappedFieldNamesForRules
  );
  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        zIndex: 1300,
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(33, 41, 52, 0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '12px',
          width: '95vw',
          height: '92vh',
          maxHeight: '92vh',
          boxShadow: '0 8px 32px rgba(33, 41, 52, 0.16)',
          padding: '24px',
          position: 'relative',
          fontFamily: '"Manrope", sans-serif',
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <CloseButton onClick={onClose}>
          <IconButton size="small">
            <CloseIcon />
          </IconButton>
        </CloseButton>

        <Header>Configure Field Mappings</Header>
        <SubHeader>
          {template?.name}
          <Badge>{template?.template_type?.toUpperCase()}</Badge>
          <Badge $accent>
            {mappedCount} / {fields.length} mapped
          </Badge>
        </SubHeader>

        <OutputNameSection>
          <OutputNameLabel>Generated Document Name</OutputNameLabel>
          <OutputNameInput
            placeholder="e.g. Signed Attorney Client Agreement - {client_name}"
            value={outputName}
            onChange={(e) => {
              setOutputName(e.target.value);
              setOutputNameDirty(true);
              setOutputNameSaved(false);
            }}
          />
          <OutputNameHint>
            Available variables: {"{client_name}"}, {"{matter_name}"}, {"{attorney}"}, {"{case_type}"}, {"{practice_area}"}, {"{date}"}. Leave blank to use default naming.
          </OutputNameHint>
          {outputNameDirty && (
            <OutputNameSaveRow>
              <SmallButton
                $primary
                onClick={handleSaveOutputName}
                disabled={outputNameSaving}
              >
                {outputNameSaving ? "Saving..." : "Save Name"}
              </SmallButton>
              {outputNameSaved && <SavedIndicator>Saved</SavedIndicator>}
            </OutputNameSaveRow>
          )}
          {!outputNameDirty && outputNameSaved && (
            <OutputNameSaveRow>
              <SavedIndicator>Saved</SavedIndicator>
            </OutputNameSaveRow>
          )}
        </OutputNameSection>

        {loading ? (
          <Loader>
            <CircularProgress size={28} />
          </Loader>
        ) : fields.length === 0 ? (
          <EmptyState>
            No fields found for this template. Make sure the template has
            form fields (PDF) or merge variables (DOCX).
          </EmptyState>
        ) : (
          <>
            <TabBar>
              <Tab $active={activeTab === "fields"} onClick={() => setActiveTab("fields")}>
                Field Mappings
                <TabCount $active={activeTab === "fields"}>{mappedCount}/{fields.length}</TabCount>
              </Tab>
              <Tab $active={activeTab === "conditionals"} onClick={() => setActiveTab("conditionals")}>
                Conditional Sections
                {(conditionalEntries.length > 0 || mergeFieldEntries.length > 0) && (
                  <TabCount $active={activeTab === "conditionals"}>
                    {conditionalEntries.length + mergeFieldEntries.length}
                  </TabCount>
                )}
              </Tab>
              <Tab $active={activeTab === "rules"} onClick={() => setActiveTab("rules")}>
                Rules &amp; Logic
                {logicRulesCount > 0 && (
                  <TabCount $active={activeTab === "rules"}>{logicRulesCount}</TabCount>
                )}
              </Tab>
              <Tab $active={activeTab === "preview"} onClick={() => setActiveTab("preview")}>
                Test &amp; Preview
              </Tab>
            </TabBar>

            <ScrollBody>
              {activeTab === "fields" && (
                <TabPanel>
                  <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                    <SearchBar
                      placeholder="Search fields..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <StyledSelect
                      style={{ width: 140, flexShrink: 0 }}
                      value={filterMode}
                      onChange={(e) => setFilterMode(e.target.value)}
                    >
                      <option value="all">All fields</option>
                      <option value="mapped">Mapped</option>
                      <option value="unmapped">Unmapped</option>
                    </StyledSelect>
                  </div>

                  <Toolbar>
                    <Tooltip title="Analyzes field names and suggests likely Salesforce matches for unmapped fields" arrow>
                      <ToolbarButton $primary onClick={autoSuggestAll}>
                        Auto-Suggest Unmapped
                      </ToolbarButton>
                    </Tooltip>
                    <ToolbarButton $danger onClick={clearAllMappings} disabled={mappedCount === 0}>
                      Clear All Mappings
                    </ToolbarButton>
                    <Tooltip title="Re-fetch all Salesforce fields from the server (clears cache)" arrow>
                      <ToolbarButton onClick={refreshAllSfFields}>
                        Refresh SF Fields
                      </ToolbarButton>
                    </Tooltip>
                  </Toolbar>
              <FieldRowHeader>
                <div>Template Field</div>
                <div>Data Source</div>
                <div>Salesforce Field</div>
                <div>Transform</div>
              </FieldRowHeader>

              {visibleFields.map((f) => {
                const m = mappings[f.field_name] || {};
                const objectType = SOURCE_TO_OBJECT[m.sf_object];
                const sfFields = sfFieldsCache[objectType] || [];

                return (
                  <FieldRow key={f.field_name}>
                    <Tooltip title={`Field key: ${f.field_name}`} arrow placement="top-start">
                      <FieldName>
                        {humanize(f.field_label || f.field_name)}
                        {m.sf_object && (
                          <ClearLink
                            onClick={() => clearMapping(f.field_name)}
                            style={{ marginLeft: 6 }}
                          >
                            clear
                          </ClearLink>
                        )}
                      </FieldName>
                    </Tooltip>

                    <StyledSelect
                      value={m.sf_object || ""}
                      onChange={(e) =>
                        updateMapping(f.field_name, "sf_object", e.target.value)
                      }
                    >
                      <option value="">--</option>
                      {groupedDataSources.map((g) =>
                        g.group ? (
                          <optgroup key={g.group} label={g.group}>
                            {g.items.map((ds) => (
                              <option key={ds.value} value={ds.value}>
                                {ds.label}
                              </option>
                            ))}
                          </optgroup>
                        ) : (
                          g.items.map((ds) => (
                            <option key={ds.value} value={ds.value}>
                              {ds.label}
                            </option>
                          ))
                        )
                      )}
                    </StyledSelect>

                    <SalesforceFieldSelect
                      value={m.sf_field_name || ""}
                      onChange={(val) =>
                        updateMapping(f.field_name, "sf_field_name", val)
                      }
                      fields={sfFields}
                      disabled={!m.sf_object}
                      popularNames={popularFieldsByObject[objectType]}
                    />

                    <StyledSelect
                      value={m.transformation || ""}
                      onChange={(e) =>
                        updateMapping(f.field_name, "transformation", e.target.value)
                      }
                      disabled={!m.sf_object}
                    >
                      {transformations.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </StyledSelect>
                  </FieldRow>
                );
              })}

              {visibleFields.length === 0 && (
                <EmptyState style={{ padding: 16 }}>
                  No fields match your search.
                </EmptyState>
              )}
                </TabPanel>
              )}

              {activeTab === "conditionals" && (
                <TabPanel style={{ paddingBottom: 220 }}>
                <ConditionalHint>
                  Map fields that control Formstack conditional blocks. In your template, use{" "}
                  <code>{'{if field_name = "value"}'}...{'{/if}'}</code> syntax, then map{" "}
                  <code>field_name</code> here to a Salesforce field so the correct value is sent during merge.
                </ConditionalHint>
                {conditionalEntries.length > 0 && (
                  <>
                    <ConditionalRowHeader>
                      <span>Field Name</span>
                      <span>Data Source</span>
                      <span>Salesforce Field</span>
                      <span>Notes</span>
                      <span />
                    </ConditionalRowHeader>
                    {conditionalEntries.map((entry, idx) => {
                      const objType = SOURCE_TO_OBJECT[entry.sf_object];
                      const sfFields = sfFieldsCache[objType] || [];
                      return (
                        <ConditionalRow key={entry._key}>
                          <ConditionalInput
                            value={entry.field_name}
                            placeholder="e.g. source_type"
                            onChange={(e) => {
                              const val = e.target.value;
                              setConditionalEntries((prev) =>
                                prev.map((c, i) => (i === idx ? { ...c, field_name: val } : c))
                              );
                            }}
                          />
                          <StyledSelect
                            value={entry.sf_object || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setConditionalEntries((prev) =>
                                prev.map((c, i) =>
                                  i === idx ? { ...c, sf_object: val, sf_field_name: "" } : c
                                )
                              );
                              const newObj = SOURCE_TO_OBJECT[val];
                              if (newObj) loadSfFields(newObj);
                            }}
                          >
                            <option value="">--</option>
                            {groupedDataSources.map((g) =>
                              g.group ? (
                                <optgroup key={g.group} label={g.group}>
                                  {g.items.map((ds) => (
                                    <option key={ds.value} value={ds.value}>
                                      {ds.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ) : (
                                g.items.map((ds) => (
                                  <option key={ds.value} value={ds.value}>
                                    {ds.label}
                                  </option>
                                ))
                              )
                            )}
                          </StyledSelect>
                          <SalesforceFieldSelect
                            value={entry.sf_field_name || ""}
                            onChange={(val) => {
                              setConditionalEntries((prev) =>
                                prev.map((c, i) => (i === idx ? { ...c, sf_field_name: val } : c))
                              );
                            }}
                            fields={sfFields}
                            disabled={!entry.sf_object}
                            popularNames={popularFieldsByObject[objType]}
                          />
                          <ConditionalInput
                            value={entry.conditional_notes}
                            placeholder="e.g. Shows section when value = Attorney Referral"
                            onChange={(e) => {
                              const val = e.target.value;
                              setConditionalEntries((prev) =>
                                prev.map((c, i) =>
                                  i === idx ? { ...c, conditional_notes: val } : c
                                )
                              );
                            }}
                          />
                          <RemoveButton
                            title="Remove"
                            onClick={() =>
                              setConditionalEntries((prev) => prev.filter((_, i) => i !== idx))
                            }
                          >
                            &times;
                          </RemoveButton>
                        </ConditionalRow>
                      );
                    })}
                  </>
                )}
                <AddConditionalButton
                  onClick={() =>
                    setConditionalEntries((prev) => [
                      ...prev,
                      {
                        _key: `cond-${Date.now()}`,
                        field_name: "",
                        sf_object: "",
                        sf_field_name: "",
                        conditional_notes: "",
                      },
                    ])
                  }
                >
                  + Add Conditional Field
                </AddConditionalButton>

                <div style={{ borderTop: "2px solid #e5e7eb", marginTop: 24, paddingTop: 16 }}>
                  <ConditionalHint style={{ marginBottom: 8 }}>
                    <strong>Merge Fields in Conditional Sections</strong> — Map merge tags that appear
                    inside <code>{'{if}'}...{'{/if}'}</code> blocks. These fields may not be detected
                    automatically and need manual mapping so Formstack can populate them during merge.
                  </ConditionalHint>
                  {mergeFieldEntries.length > 0 && (
                    <>
                      <ConditionalRowHeader>
                        <span>Merge Tag Name</span>
                        <span>Data Source</span>
                        <span>Salesforce Field</span>
                        <span>Transform</span>
                        <span />
                      </ConditionalRowHeader>
                      {mergeFieldEntries.map((entry, idx) => {
                        const objType = SOURCE_TO_OBJECT[entry.sf_object];
                        const sfFields = sfFieldsCache[objType] || [];
                        return (
                          <ConditionalRow key={entry._key}>
                            <ConditionalInput
                              value={entry.field_name}
                              placeholder="e.g. referring_attorney"
                              onChange={(e) => {
                                const val = e.target.value;
                                setMergeFieldEntries((prev) =>
                                  prev.map((m, i) => (i === idx ? { ...m, field_name: val } : m))
                                );
                              }}
                            />
                            <StyledSelect
                              value={entry.sf_object || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMergeFieldEntries((prev) =>
                                  prev.map((m, i) =>
                                    i === idx ? { ...m, sf_object: val, sf_field_name: "" } : m
                                  )
                                );
                                const newObj = SOURCE_TO_OBJECT[val];
                                if (newObj) loadSfFields(newObj);
                              }}
                            >
                              <option value="">--</option>
                              {groupedDataSources.map((g) =>
                                g.group ? (
                                  <optgroup key={g.group} label={g.group}>
                                    {g.items.map((ds) => (
                                      <option key={ds.value} value={ds.value}>
                                        {ds.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ) : (
                                  g.items.map((ds) => (
                                    <option key={ds.value} value={ds.value}>
                                      {ds.label}
                                    </option>
                                  ))
                                )
                              )}
                            </StyledSelect>
                            <SalesforceFieldSelect
                              value={entry.sf_field_name || ""}
                              onChange={(val) => {
                                setMergeFieldEntries((prev) =>
                                  prev.map((m, i) => (i === idx ? { ...m, sf_field_name: val } : m))
                                );
                              }}
                              fields={sfFields}
                              disabled={!entry.sf_object}
                              popularNames={popularFieldsByObject[objType]}
                            />
                            <StyledSelect
                              value={entry.transformation || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMergeFieldEntries((prev) =>
                                  prev.map((m, i) => (i === idx ? { ...m, transformation: val } : m))
                                );
                              }}
                              disabled={!entry.sf_object}
                            >
                              {transformations.map((t) => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </StyledSelect>
                            <RemoveButton
                              title="Remove"
                              onClick={() =>
                                setMergeFieldEntries((prev) => prev.filter((_, i) => i !== idx))
                              }
                            >
                              &times;
                            </RemoveButton>
                          </ConditionalRow>
                        );
                      })}
                    </>
                  )}
                  <AddConditionalButton
                    onClick={() =>
                      setMergeFieldEntries((prev) => [
                        ...prev,
                        {
                          _key: `merge-${Date.now()}`,
                          field_name: "",
                          sf_object: "",
                          sf_field_name: "",
                          transformation: "",
                        },
                      ])
                    }
                  >
                    + Add Merge Field
                  </AddConditionalButton>
                </div>
                </TabPanel>
              )}

              {activeTab === "rules" && (
                <TabPanel style={{ paddingBottom: 220 }}>
                  <RuleNote>
                    Build a rule once, then choose every template field that should use it.
                    If a rule does not pass, DMS sends those selected fields as blank values.
                    Conditional Sections stay separate and are not changed here.
                  </RuleNote>
                  {mappedFieldsForRules.length === 0 ? (
                    <EmptyState style={{ padding: 16 }}>
                      Map at least one template field before adding Rules &amp; Logic.
                    </EmptyState>
                  ) : (
                    <>
                      {visibleLogicRuleGroups.map((rules, groupIndex) => {
                        const completeRuleCount =
                          sanitizeLogicRulesForSave(rules).conditions?.length || 0;
                        const validTargetNames = new Set(mappedFieldNamesForRules);
                        const selectedTargets = (rules.targetFieldNames || []).filter((fieldName) =>
                          validTargetNames.has(fieldName)
                        );

                        return (
                          <RuleCard key={rules._key}>
                          <RuleCardHeader>
                            <div>
                              <RuleTarget>Rule {groupIndex + 1}</RuleTarget>
                              <ConditionalHint style={{ marginTop: 2 }}>
                                Applies to {selectedTargets.length} template field
                                {selectedTargets.length === 1 ? "" : "s"}
                                {completeRuleCount > 0 && (
                                  <Badge $accent style={{ marginLeft: 8 }}>
                                    {completeRuleCount} rule{completeRuleCount === 1 ? "" : "s"}
                                  </Badge>
                                )}
                              </ConditionalHint>
                            </div>
                            <StyledSelect
                              style={{ width: 150 }}
                              value={rules.mode === "any" ? "any" : "all"}
                              onChange={(e) =>
                                updateLogicRuleGroup(rules._key, { mode: e.target.value })
                              }
                            >
                              <option value="all">All rules</option>
                              <option value="any">Any rules</option>
                            </StyledSelect>
                            {visibleLogicRuleGroups.length > 1 && (
                              <RemoveButton
                                title="Remove rule group"
                                onClick={() => removeLogicRuleGroup(rules._key)}
                              >
                                &times;
                              </RemoveButton>
                            )}
                          </RuleCardHeader>

                          {(rules.conditions || []).map((condition, conditionIndex) => {
                            const leftObjectType = SOURCE_TO_OBJECT[condition.left?.object];
                            const leftFields = sfFieldsCache[leftObjectType] || [];
                            const rightObjectType = SOURCE_TO_OBJECT[condition.right?.object];
                            const rightFields = sfFieldsCache[rightObjectType] || [];
                            const rightType = condition.right?.type || "literal";

                            return (
                              <RuleRow key={condition._key || `${rules._key}-${conditionIndex}`}>
                                <StyledSelect
                                  value={condition.left?.object || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                      ...current,
                                      left: { object: value, field: "" },
                                    }));
                                    const objectType = SOURCE_TO_OBJECT[value];
                                    if (objectType) loadSfFields(objectType);
                                  }}
                                >
                                  <option value="">Left object</option>
                                  {groupedDataSources.map((g) =>
                                    g.group ? (
                                      <optgroup key={g.group} label={g.group}>
                                        {g.items.map((ds) => (
                                          <option key={ds.value} value={ds.value}>
                                            {ds.label}
                                          </option>
                                        ))}
                                      </optgroup>
                                    ) : (
                                      g.items.map((ds) => (
                                        <option key={ds.value} value={ds.value}>
                                          {ds.label}
                                        </option>
                                      ))
                                    )
                                  )}
                                </StyledSelect>
                                <SalesforceFieldSelect
                                  value={condition.left?.field || ""}
                                  onChange={(value) =>
                                    updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                      ...current,
                                      left: { ...(current.left || {}), field: value },
                                    }))
                                  }
                                  fields={leftFields}
                                  disabled={!condition.left?.object}
                                  popularNames={popularFieldsByObject[leftObjectType]}
                                />
                                <StyledSelect
                                  value={condition.operator || "equals"}
                                  onChange={(e) =>
                                    updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                      ...current,
                                      operator: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="equals">equals</option>
                                  <option value="does_not_equal">does not equal</option>
                                </StyledSelect>
                                <StyledSelect
                                  value={rightType}
                                  onChange={(e) => {
                                    const nextType = e.target.value;
                                    updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                      ...current,
                                      right:
                                        nextType === "field"
                                          ? { type: "field", object: "MATTER", field: "" }
                                          : { type: "literal", value: false },
                                    }));
                                    if (nextType === "field") loadSfFields("matter");
                                  }}
                                >
                                  <option value="literal">Enter a value</option>
                                  <option value="field">Salesforce Field</option>
                                </StyledSelect>
                                {rightType === "literal" ? (
                                  <>
                                    <StyledSelect
                                      value={condition.right?.value === true ? "true" : "false"}
                                      onChange={(e) =>
                                        updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                          ...current,
                                          right: {
                                            type: "literal",
                                            value: e.target.value === "true",
                                          },
                                        }))
                                      }
                                    >
                                      <option value="false">false</option>
                                      <option value="true">true</option>
                                    </StyledSelect>
                                    <div />
                                  </>
                                ) : (
                                  <>
                                    <StyledSelect
                                      value={condition.right?.object || ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                          ...current,
                                          right: { type: "field", object: value, field: "" },
                                        }));
                                        const objectType = SOURCE_TO_OBJECT[value];
                                        if (objectType) loadSfFields(objectType);
                                      }}
                                    >
                                      <option value="">Right object</option>
                                      {groupedDataSources.map((g) =>
                                        g.group ? (
                                          <optgroup key={g.group} label={g.group}>
                                            {g.items.map((ds) => (
                                              <option key={ds.value} value={ds.value}>
                                                {ds.label}
                                              </option>
                                            ))}
                                          </optgroup>
                                        ) : (
                                          g.items.map((ds) => (
                                            <option key={ds.value} value={ds.value}>
                                              {ds.label}
                                            </option>
                                          ))
                                        )
                                      )}
                                    </StyledSelect>
                                    <SalesforceFieldSelect
                                      value={condition.right?.field || ""}
                                      onChange={(value) =>
                                        updateLogicRuleCondition(rules._key, conditionIndex, (current) => ({
                                          ...current,
                                          right: { ...(current.right || {}), type: "field", field: value },
                                        }))
                                      }
                                      fields={rightFields}
                                      disabled={!condition.right?.object}
                                      popularNames={popularFieldsByObject[rightObjectType]}
                                    />
                                  </>
                                )}
                                <RemoveButton
                                  title="Remove rule"
                                  onClick={() =>
                                    removeLogicRuleCondition(rules._key, conditionIndex)
                                  }
                                >
                                  &times;
                                </RemoveButton>
                              </RuleRow>
                            );
                          })}

                          <AddConditionalButton
                            onClick={() => addLogicRuleCondition(rules._key)}
                          >
                            + Add Rule
                          </AddConditionalButton>
                          <TemplateFieldMultiSelect
                            fields={mappedFieldsForRules}
                            selectedFieldNames={selectedTargets}
                            searchValue={ruleTargetSearchByGroup[rules._key] || ""}
                            onSearchChange={(value) =>
                              setRuleTargetSearchByGroup((prev) => ({
                                ...prev,
                                [rules._key]: value,
                              }))
                            }
                            onToggleField={(fieldName, selected) =>
                              toggleLogicRuleTarget(rules._key, fieldName, selected)
                            }
                          />
                        </RuleCard>
                        );
                      })}
                      <AddConditionalButton onClick={addLogicRuleGroup}>
                        + Add another rule
                      </AddConditionalButton>
                    </>
                  )}
                </TabPanel>
              )}

              {activeTab === "preview" && (
                <TabPanel>
                <MatterSearchRow>
                  <MatterSearchInput>
                    <MatterInput
                      placeholder="Search for a matter / client name..."
                      value={
                        selectedMatter
                          ? selectedMatter.display_name || selectedMatter.name || `Matter #${selectedMatter.id}`
                          : matterQuery
                      }
                      onChange={(e) => {
                        setMatterQuery(e.target.value);
                        setSelectedMatter(null);
                        setPreviewData(null);
                        setPreviewError(null);
                        setDocumentPreview(null);
                        setDocPreviewError(null);
                        setDocUnmappedFieldNames([]);
                        setDocUnmappedFieldCount(0);
                        setInlinePreviewPageCount(0);
                        setInlinePreviewRenderError(null);
                      }}
                      onFocus={() => {
                        if (matterResults.length > 0) setMatterDropdownOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setMatterDropdownOpen(false), 200)}
                    />
                    {matterDropdownOpen && matterResults.length > 0 && (
                      <MatterDropdown>
                        {matterResults.map((m) => (
                          <MatterItem
                            key={m.id}
                            onMouseDown={() => {
                              setSelectedMatter(m);
                              setMatterDropdownOpen(false);
                              setMatterQuery("");
                            }}
                          >
                            <MatterName>
                              {m.display_name || m.name || `Matter #${m.id}`}
                            </MatterName>
                            {m.client_name && (
                              <MatterMeta>{m.client_name}</MatterMeta>
                            )}
                          </MatterItem>
                        ))}
                      </MatterDropdown>
                    )}
                    {matterSearching && (
                      <div style={{ position: "absolute", right: 8, top: 8 }}>
                        <CircularProgress size={14} />
                      </div>
                    )}
                  </MatterSearchInput>
                  {letterheads.length > 0 && (
                    <LetterheadSelect
                      value={selectedLetterhead}
                      onChange={(e) => setSelectedLetterhead(e.target.value)}
                    >
                      <option value="">No letterhead</option>
                      {letterheads.map((lh) => (
                        <option key={lh.id} value={lh.id}>
                          {lh.name || `${lh.firm_name} letterhead`}
                        </option>
                      ))}
                    </LetterheadSelect>
                  )}
                  <RunButton onClick={runPreview} disabled={!selectedMatter || previewLoading || docPreviewLoading}>
                    {previewLoading || docPreviewLoading ? "Running..." : "Run Preview"}
                  </RunButton>
                </MatterSearchRow>

                {previewLoading && (
                  <div style={{ textAlign: "center", padding: 16 }}>
                    <CircularProgress size={22} />
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                      Fetching Salesforce data...
                    </div>
                  </div>
                )}

                {previewError && !previewLoading && (
                  <div style={{
                    textAlign: "center",
                    padding: 12,
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: 8,
                    margin: "8px 0",
                  }}>
                    <div style={{ fontSize: 13, color: "#92400e", fontWeight: 500 }}>
                      Field-value preview failed
                    </div>
                    <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>
                      {previewError}
                    </div>
                  </div>
                )}

                {docPreviewLoading && (
                  <div style={{ textAlign: "center", padding: 16 }}>
                    <CircularProgress size={22} />
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                      Generating filled document... This may take a moment for large templates.
                    </div>
                  </div>
                )}

                {docPreviewError && !docPreviewLoading && !documentPreview && (
                  <div style={{
                    textAlign: "center",
                    padding: 16,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    margin: "8px 0",
                  }}>
                    <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 500 }}>
                      Document preview failed
                    </div>
                    <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>
                      {docPreviewError}
                    </div>
                    <button
                      onClick={runPreview}
                      style={{
                        marginTop: 8,
                        padding: "4px 12px",
                        fontSize: 12,
                        border: "1px solid #fca5a5",
                        borderRadius: 6,
                        background: "#fff",
                        color: "#991b1b",
                        cursor: "pointer",
                      }}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {documentPreview && !docPreviewLoading && (
                  <DocumentPreviewWrapper>
                    <DocumentPreviewHeader>
                      <span>Document Preview</span>
                      <ViewFullButton onClick={() => setShowFullPreview(true)}>
                        Open Full Preview
                      </ViewFullButton>
                    </DocumentPreviewHeader>
                    <div style={{ height: 400, overflow: "auto", background: "#e5e7eb", display: "flex", justifyContent: "center", padding: "12px 0" }}>
                      {inlinePreviewFile ? (
                        <div style={{ width: "90%", minHeight: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start", background: "#fff", borderRadius: 4, padding: 8 }}>
                          <Document
                            file={inlinePreviewFile}
                            loading={
                              <div style={{ paddingTop: 24, fontSize: 12, color: "#6b7280" }}>
                                Rendering PDF preview...
                              </div>
                            }
                            error={
                              <div style={{ paddingTop: 24, fontSize: 12, color: "#b91c1c" }}>
                                Inline preview failed to render. Use &quot;Open Full Preview&quot;.
                              </div>
                            }
                            onLoadSuccess={({ numPages }) => {
                              setInlinePreviewPageCount(numPages || 0);
                              setInlinePreviewRenderError(null);
                            }}
                            onLoadError={(error) => {
                              console.error("Inline PDF render failed", error);
                              setInlinePreviewRenderError(
                                "Inline preview failed to render. Use Open Full Preview."
                              );
                            }}
                          >
                            <Page
                              pageNumber={1}
                              width={700}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                            />
                          </Document>
                        </div>
                      ) : (
                        <div style={{ paddingTop: 24, fontSize: 12, color: "#b91c1c" }}>
                          Unable to decode inline preview. Use &quot;Open Full Preview&quot;.
                        </div>
                      )}
                    </div>
                    {inlinePreviewRenderError && (
                      <div style={{ fontSize: 12, color: "#b91c1c", padding: "8px 12px" }}>
                        {inlinePreviewRenderError}
                      </div>
                    )}
                    {inlinePreviewPageCount > 1 && !inlinePreviewRenderError && (
                      <div style={{ fontSize: 12, color: "#6b7280", padding: "8px 12px" }}>
                        Showing page 1 of {inlinePreviewPageCount}. Use &quot;Open Full Preview&quot; for full navigation.
                      </div>
                    )}
                  </DocumentPreviewWrapper>
                )}

                {!previewData && !previewLoading && !selectedMatter && (
                  <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: 8 }}>
                    Search for a matter above, then click &quot;Run Preview&quot; to see how your
                    mappings populate with real Salesforce data.
                  </div>
                )}
                </TabPanel>
              )}
            </ScrollBody>
          </>
        )}

        <Footer>
          <FooterLeft>
            {mappedCount} of {fields.length} fields configured
          </FooterLeft>
          <div>
            {saveError && (
              <span style={{ color: "#b91c1c", fontSize: 12, marginRight: 12 }}>
                Save failed — check this configuration
              </span>
            )}
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <SaveButton onClick={handleSave} disabled={saving || loading}>
              {saving ? "Saving..." : "Save Configuration"}
            </SaveButton>
          </div>
        </Footer>
      </div>
      {showFullPreview && documentPreview && (
        <div onClick={(e) => e.stopPropagation()}>
          <PrintPreview
            printData={documentPreview}
            setOpenPreview={setShowFullPreview}
          />
        </div>
      )}
    </div>,
    document.body
  );
}

FieldMappingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  template: PropTypes.shape({
    formstack_id: PropTypes.string,
    name: PropTypes.string,
    template_type: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
